import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { color, fontSize, radius } from '../../theme/tokens.js';

/**
 * Loading, empty, and error states (FR-056, PRD §6.5).
 *
 * The distinction these keep apart is the important one: **"no data" and "we could not fetch
 * it" must not look the same.** An empty state where a request failed reads as a real business
 * outcome — a region with nothing in it — and that is precisely the misreading PRD §6.5 and
 * FR-055 exist to prevent.
 */

export function Skeleton({
  height,
  width,
  style,
}: {
  height: number | string;
  width?: number | string;
  /** Spread last — for a placeholder that must keep a pill's radius or not shrink in a flex row. */
  style?: CSSProperties;
}) {
  return (
    <div
      className="skeleton"
      style={{ height, width: width ?? '100%', ...style }}
      aria-hidden="true"
    />
  );
}

/**
 * Hold the space the loaded content last occupied, for as long as it is being refetched
 * (owner request 2026-09-03).
 *
 * The problem it solves is a **filter change**, not a first paint. Every section on these pages
 * swaps its content for a `<Skeleton>` of some fixed height while a request is in flight, and a
 * fixed height is right exactly once — on the cold load nobody sees, before there is anything to
 * compare it to. On a period change the section is already the size of a full table, so it
 * collapses to the skeleton's height and springs back a moment later; everything below it moves
 * twice, and the page reads as though it broke and recovered.
 *
 * Measuring is the only honest answer, because the height is the *data's*: seven regions and
 * three hundred clients do not make the same table, and no constant is right for both. So the
 * section reports its own height while it has content, and reserves that number while it does
 * not. It cannot drift from what it is reserving for, because it *is* what it is reserving for.
 *
 * ⚠ It reserves a **minimum**, never a height. New data that is taller still grows the section;
 * shorter data shrinks it the moment the reservation lifts. Nothing is ever clipped, and nothing
 * is padded once the answer is on screen.
 *
 * `ResizeObserver` is guarded for jsdom, which has neither it nor a layout engine — under test
 * every height is 0, nothing is ever recorded, and the reservation is simply absent. That is the
 * right degradation: a test asserting a reserved pixel count would be asserting the browser's
 * layout, not this code's rule.
 */
export function useReservedHeight(loading: boolean): {
  ref: (node: HTMLElement | null) => void;
  style: CSSProperties;
} {
  const [reserved, setReserved] = useState<number | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  /*
   * Read through a ref, so the measuring callback never has to be rebuilt when the flag flips —
   * a new callback ref detaches and reattaches the node, which is a disconnect and a remeasure on
   * every render of the page.
   */
  const isLoading = useRef(loading);
  isLoading.current = loading;

  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    // A null node is the content unmounting to make room for the skeleton — the one moment the
    // stored height matters most, so it is deliberately NOT cleared here.
    if (!node) return;

    const measure = () => {
      // While loading, the box is the reservation itself; recording it would be recording our own
      // answer, and a section that failed to load would then hold that height for the session.
      if (isLoading.current) return;
      const next = node.offsetHeight;
      if (next > 0) setReserved(next);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    observer.current = ro;
  }, []);

  return {
    ref,
    style: loading && reserved !== null ? { minHeight: reserved } : {},
  };
}

export function ErrorBanner({
  message,
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: color.dangerBg,
        border: `1px solid ${color.negativeStrong}`,
        color: color.dangerInk,
        borderRadius: radius['3xl'],
        padding: '14px 18px',
        fontSize: fontSize.bodyStrong,
      }}
    >
      <span aria-hidden="true">⚠</span>
      <span style={{ flex: 1 }}>{message ?? 'Something went wrong loading this view.'}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            border: `1px solid ${color.negativeStrong}`,
            background: 'transparent',
            color: color.dangerInk,
            borderRadius: radius.xl,
            padding: '6px 14px',
            fontSize: fontSize.body,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['6xl'],
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: fontSize.cardTitle, fontWeight: 700, color: color.ink }}>{title}</div>
      {detail ? (
        <div
          style={{
            marginTop: 8,
            fontSize: fontSize.bodyStrong,
            color: color.inkMuted,
            maxWidth: 520,
            margin: '8px auto 0',
          }}
        >
          {detail}
        </div>
      ) : null}
      {action ? <div style={{ marginTop: 18 }}>{action}</div> : null}
    </div>
  );
}

/**
 * The "no successful sync has ever run" state (PRD §6.5).
 *
 * Deliberately distinct from a region-level empty state, and deliberately **not** a page of
 * zeros: on a first deploy, zeros are indistinguishable from a total collapse in the business.
 */
export function NoSnapshotState() {
  return (
    <EmptyState
      title="No data yet"
      detail="No refresh has completed successfully, so there is nothing to show. Once the daily sync runs, this view will fill in and the as-of date will appear in the header."
    />
  );
}
